import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { List } from '../../constants-types/PiList.model';

// The notion of selection and isSelected and getSelectionClass
// was tacked on to this. It might be nice to separate them out
// but I couldn't see a clean way at the time. Perhaps a smarter
// version of me or a version of someone else will have insight
// at a later time.

@Component({
    selector: 'ok-pillbox',
    template: `
        @if (!!boundValue) {
            <div
                class="ok-pillbox"
                [ngClass]="{
                    'ok-pillbox--hasPills': boundValue.size
                }"
            >
                @for (
                    individualSelection of boundValue;
                    track individualSelection;
                    let i = $index
                ) {
                    <ok-pill
                        [item]="individualSelection"
                        [index]="i"
                        [isValid]="pillIsValid(individualSelection)"
                        (onRemove)="remove(i, individualSelection)"
                        (onEdit)="edit(individualSelection)"
                        (click)="clickEvent($event, individualSelection, i)"
                        [editable]="editablePills"
                        [removable]="removablePills"
                        [isColorPickerEnabled]="isColorPickerEnabled"
                        [isPillSelected]="
                            getSelectionClass(individualSelection)
                        "
                        (onColorChange)="onColorChange.emit()"
                        [showPicker]="currentIndex === i"
                        [isDisabled]="isDisabled"
                    >
                        <ng-template let-item="item">
                            <ng-template
                                [ngTemplateOutlet]="contentTemplate"
                                [ngTemplateOutletContext]="{
                                    item: item,
                                    index: i
                                }"
                            >
                            </ng-template>
                        </ng-template>
                    </ok-pill>
                }
            </div>
        }
    `,
    // Following is required for ControlValueAccessor
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OkPillboxComponent,
            multi: true
        }
    ],
    standalone: false
})
export class OkPillboxComponent<T> {
    @ContentChild(TemplateRef, { static: false })
    contentTemplate: TemplateRef<any>;

    @Input()
    isColorPickerEnabled: boolean;

    @Input()
    isSelected: (input: any) => boolean;

    @Input()
    isValidFunc: (input: any) => boolean = (_input: any) => true;

    @Input()
    editablePills = false;
    @Input()
    removablePills = true;
    @Input()
    currentIndex: number;
    @Input()
    isDisabled = false;

    @Output()
    onRemove: EventEmitter<any> = new EventEmitter();
    @Output()
    onEdit: EventEmitter<any> = new EventEmitter();
    @Output()
    onPillSelect: EventEmitter<any> = new EventEmitter();
    @Output()
    onTooltipChange: EventEmitter<any> = new EventEmitter();
    @Output()
    onColorChange = new EventEmitter<void>();

    clickEvent(event: Event, individualSelection: any, i: number) {
        event.stopPropagation();
        this.onPillSelect.emit(individualSelection);
        this.onTooltipChange.emit(i);
    }

    getSelectionClass(individualSelection: any) {
        if (this.isSelected) {
            return this.isSelected(individualSelection)
                ? 'ok-pill--selected'
                : 'ok-pill--notselected';
        }
        return '';
    }

    private selectedItems: List<T>; // = ImmutableList();

    private propagateChange: Function = () => {
        /* No-op required by ControlValueAccessor interface - see registerOnChange */
    };

    get boundValue(): List<T> {
        return this.selectedItems;
    }
    set boundValue(val: List<T>) {
        this.selectedItems = val;
        this.propagateChange(this.selectedItems);
    }

    remove(indexToRemove: number, itemToRemove: any): void {
        this.selectedItems = this.selectedItems.splice(indexToRemove, 1);
        this.propagateChange(this.selectedItems);
        this.onRemove.emit(itemToRemove);
    }

    edit(itemToEdit: any): void {
        this.onEdit.emit(itemToEdit);
    }

    pillIsValid(selection: any): boolean {
        return this.isValidFunc(selection);
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: any): void {
        this.selectedItems = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed: any) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
